import compose from 'lodash/fp/flow';
import keyBy from 'lodash/fp/keyBy';
import mapValues from 'lodash/fp/mapValues';
import merge from 'lodash/fp/merge';
import pick from 'lodash/fp/pick';
import {
  RECEIVE_PRIMARY_CONTENT,
  RECEIVE_SECONDARY_CONTENT,
  RECEIVE_SITE_OPTIONS,
} from 'config/types/action';
import { contentGroup as defaultState } from 'config/defaultState';
import contentTypeMap from 'config/types/content';
import filterContentFields from 'utils/scriptFilter';

const isContentType = (type) => Object.values(contentTypeMap).includes(type);

const postProcessContentEntities = (entities) => ({
  ...entities,
  postProcessedData: mapValues(filterContentFields)(entities.byId),
});

// For documentation on this see docs/article-content-embeds.md
const postProcessContent = (contentGroupState) => Object.entries(contentGroupState)
  .reduce((postProcessedContentGroupState, [type, entities]) => {
    // eslint-disable-next-line no-param-reassign -- This is a valid case for param reassign as a new object was created
    postProcessedContentGroupState[type] = isContentType(type) ?
      postProcessContentEntities(entities) :
      entities;
    return postProcessedContentGroupState;
  }, {});

/**
 * Create a state slice reducer that will set the initial state shape based on
 * the "content group" types (taxonomies and content types) returned from
 * RECEIVE_SITE_OPTIONS. Child types related to their parent group type will
 * be merged into the state on receive content actions.
 *
 * @param {string}     groupType  group type from RECEIVE_SITE_OPTIONS payload
 * @returns {function}            Redux reducer function
 */
export default (groupType) => (state = {}, { type, payload }) => {
  switch (type) {
    case RECEIVE_SITE_OPTIONS: {
      const templateTypes = payload[groupType];
      const assignDefaultState = (acc, { id }) => ({
        ...acc,
        [id]: defaultState,
      });

      return templateTypes.reduce(assignDefaultState, {});
    }

    case RECEIVE_PRIMARY_CONTENT:
    case RECEIVE_SECONDARY_CONTENT: {
      const types = Object.keys(state);
      const indexBySlug = compose(keyBy('slug'), mapValues('id'));

      const normalize = (entities) => ({
        byId: keyBy('id', entities),
        idBySlug: indexBySlug(entities),
      });

      const mergeTypes = compose(
        pick(types),
        mapValues(normalize),
        postProcessContent,
        merge(state),
      );

      return mergeTypes(payload);
    }

    default:
  }

  return state;
};
